/**
 * GoalStorage Service
 * CRUD operations for goals and accounts
 */

import { v4 as uuidv4 } from 'uuid';
import { storage } from './StorageAdapter';
import { appendEvent } from './CompletionLog';
import { Goal, GoalType } from '../../models/Goal';
import { GoalAccount } from '../../models/GoalAccount';

// Storage keys
const ACCOUNTS_KEY = 'accounts';
const GOALS_KEY_PREFIX = 'goals:';
const CURRENT_ACCOUNT_KEY = 'current_account_id';

/**
 * Account Management
 */

export const createAccount = async (name: string, themeColor?: string): Promise<GoalAccount> => {
  const account: GoalAccount = {
    id: uuidv4(),
    name: name.trim(),
    themeColor: themeColor || '#007AFF', // Default to blue if not specified
    createdAt: Date.now(),
    updatedAt: Date.now(),
    isDeleted: false,
    syncEnabled: false,
    lastSyncTimestamp: 0,
  };

  // Get existing accounts
  const accounts = await getAllAccounts();
  accounts.push(account);

  // Save
  await storage.setString(ACCOUNTS_KEY, JSON.stringify(accounts));

  // Log event
  await appendEvent(account.id, 'ACCOUNT_CREATED', {
    accountId: account.id,
    name: account.name,
  });

  // If this is the first account, set it as current
  const currentAccountId = await getCurrentAccountId();
  if (!currentAccountId) {
    await setCurrentAccountId(account.id);
  }

  return account;
};

export const getAllAccounts = async (): Promise<GoalAccount[]> => {
  const accountsJson = await storage.getString(ACCOUNTS_KEY);
  if (!accountsJson) return [];

  const accounts: GoalAccount[] = JSON.parse(accountsJson);
  return accounts.filter(a => !a.isDeleted);
};

export const getAccountById = async (accountId: string): Promise<GoalAccount | null> => {
  const accounts = await getAllAccounts();
  return accounts.find(a => a.id === accountId) || null;
};

export const updateAccount = async (
  accountId: string,
  updates: Partial<Omit<GoalAccount, 'id' | 'createdAt'>>
): Promise<void> => {
  const accounts = await getAllAccounts();
  const index = accounts.findIndex(a => a.id === accountId);

  if (index === -1) {
    throw new Error(`Account not found: ${accountId}`);
  }

  accounts[index] = {
    ...accounts[index],
    ...updates,
    updatedAt: Date.now(),
  };

  await storage.setString(ACCOUNTS_KEY, JSON.stringify(accounts));

  await appendEvent(accountId, 'ACCOUNT_UPDATED', {
    accountId,
    updates,
  });
};

export const deleteAccount = async (accountId: string): Promise<void> => {
  const accounts = await getAllAccounts();
  const index = accounts.findIndex(a => a.id === accountId);

  if (index === -1) {
    throw new Error(`Account not found: ${accountId}`);
  }

  // Soft delete
  accounts[index].isDeleted = true;
  accounts[index].updatedAt = Date.now();

  await storage.setString(ACCOUNTS_KEY, JSON.stringify(accounts));

  await appendEvent(accountId, 'ACCOUNT_DELETED', {
    accountId,
  });

  // If this was the current account, switch to another
  const currentAccountId = await getCurrentAccountId();
  if (currentAccountId === accountId) {
    const remainingAccounts = accounts.filter(a => !a.isDeleted);
    if (remainingAccounts.length > 0) {
      await setCurrentAccountId(remainingAccounts[0].id);
    }
  }
};

/**
 * Current Account
 */

export const getCurrentAccountId = async (): Promise<string | null> => {
  return await storage.getString(CURRENT_ACCOUNT_KEY) || null;
};

export const setCurrentAccountId = async (accountId: string): Promise<void> => {
  await storage.setString(CURRENT_ACCOUNT_KEY, accountId);
};

/**
 * Goal Management
 */

export const createGoal = async (
  accountId: string,
  name: string,
  type: GoalType
): Promise<Goal> => {
  const goal: Goal = {
    id: uuidv4(),
    accountId,
    name: name.trim(),
    type,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    isDeleted: false,
  };

  // Get existing goals for this account
  const goals = await getGoalsByAccountId(accountId);

  // Validate: only one main goal per account
  if (type === 'main') {
    const existingMain = goals.find(g => g.type === 'main' && !g.isDeleted);
    if (existingMain) {
      throw new Error('Account already has a main goal. Delete or update the existing one.');
    }
  }

  // Validate: max 10 side goals
  if (type === 'side') {
    const sideGoals = goals.filter(g => g.type === 'side' && !g.isDeleted);
    if (sideGoals.length >= 10) {
      throw new Error('Maximum 10 side goals per account.');
    }
  }

  goals.push(goal);

  // Save
  const goalsKey = `${GOALS_KEY_PREFIX}${accountId}`;
  await storage.setString(goalsKey, JSON.stringify(goals));

  // Log event
  await appendEvent(accountId, 'GOAL_CREATED', {
    goalId: goal.id,
    name: goal.name,
    type: goal.type,
  });

  return goal;
};

export const getGoalsByAccountId = async (accountId: string): Promise<Goal[]> => {
  const goalsKey = `${GOALS_KEY_PREFIX}${accountId}`;
  const goalsJson = await storage.getString(goalsKey);

  if (!goalsJson) return [];

  const goals: Goal[] = JSON.parse(goalsJson);
  return goals.filter(g => !g.isDeleted);
};

export const getGoalById = async (accountId: string, goalId: string): Promise<Goal | null> => {
  const goals = await getGoalsByAccountId(accountId);
  return goals.find(g => g.id === goalId) || null;
};

export const getMainGoal = async (accountId: string): Promise<Goal | null> => {
  const goals = await getGoalsByAccountId(accountId);
  return goals.find(g => g.type === 'main') || null;
};

export const getSideGoals = async (accountId: string): Promise<Goal[]> => {
  const goals = await getGoalsByAccountId(accountId);
  return goals.filter(g => g.type === 'side');
};

export const updateGoal = async (
  accountId: string,
  goalId: string,
  updates: Partial<Omit<Goal, 'id' | 'accountId' | 'createdAt'>>
): Promise<void> => {
  const goals = await getGoalsByAccountId(accountId);
  const index = goals.findIndex(g => g.id === goalId);

  if (index === -1) {
    throw new Error(`Goal not found: ${goalId}`);
  }

  goals[index] = {
    ...goals[index],
    ...updates,
    updatedAt: Date.now(),
  };

  const goalsKey = `${GOALS_KEY_PREFIX}${accountId}`;
  await storage.setString(goalsKey, JSON.stringify(goals));

  await appendEvent(accountId, 'GOAL_UPDATED', {
    goalId,
    updates,
  });
};

export const deleteGoal = async (accountId: string, goalId: string): Promise<void> => {
  const goals = await getGoalsByAccountId(accountId);
  const index = goals.findIndex(g => g.id === goalId);

  if (index === -1) {
    throw new Error(`Goal not found: ${goalId}`);
  }

  // Soft delete
  goals[index].isDeleted = true;
  goals[index].updatedAt = Date.now();

  const goalsKey = `${GOALS_KEY_PREFIX}${accountId}`;
  await storage.setString(goalsKey, JSON.stringify(goals));

  await appendEvent(accountId, 'GOAL_DELETED', {
    goalId,
  });
};

/**
 * Initialize with default account and goal if none exist
 */
export const initializeDefaultData = async (): Promise<void> => {
  const accounts = await getAllAccounts();

  if (accounts.length === 0) {
    // Create default account
    const account = await createAccount('Personal');

    // Create default main goal
    await createGoal(account.id, 'Complete my daily goal', 'main');
  }
};
